import { useParams } from "react-router-dom";

import { ActionBar } from "../../components/ActionBar";
import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { dinnerSettings } from "../../domain/dinnerNudge";
import { todayStr } from "../../domain/date";
import { familySteps, familyTraining, familyWeek, pronouns, weekRangeLabel, type FamilyTraining } from "../../domain/family";
import { dailyTargets } from "../../domain/nutrition";
import { remindersSummary } from "../../domain/water";
import { useLinked } from "../../lib/linked";
import { useGo } from "../../lib/useGo";
import { LinkedShell, familyPath } from "./linkedPage";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

/** The Training row's value: the week's strength days and walk minutes, or what is missing. */
function trainingLine(f: FamilyTraining): string {
  if (f.programme === null) return "No exercise answers yet";
  return [
    f.strength ? `${f.strength.done} of ${f.strength.of} strength days` : f.weekLine,
    f.walkMin !== null ? `${f.walkMin} walk min` : null,
    f.walks?.count ? `${f.walks.count} walk${f.walks.count === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" · ") || "Nothing due yet this week";
}

/**
 * You › Family › one person (board FamilyView; ADR-046). Their week in one answer, then what the
 * link lets you do for them. Read from the server on every open: the link is authorised per
 * request, and the parent can remove it from their phone at any time.
 */
export default function FamilyPerson() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  const go = useGo();
  const today = todayStr();

  return (
    <LinkedShell
      q={q}
      title={(name) => name ?? "Family"}
      back={{ to: "/you/family", label: "Back to Family" }}
      footer={(l) => {
        const p = pronouns(l.S);
        return (
          <ActionBar
            secondary={{ label: `Fill ${p.their} answers`, onClick: () => go(familyPath(id, "answers")) }}
            primary={{ label: `See ${p.their} plan`, onClick: () => go(familyPath(id, "plan")) }}
          />
        );
      }}
    >
      {({ S, name, synced }) => {
        const p = pronouns(S);
        const w = familyWeek(S, today);
        const steps = familySteps(S, today);
        const T = dailyTargets(S, new Date(), undefined, {});
        const dinner = dinnerSettings(S);
        const line = [
          w.shortDinners ? `Protein short on ${w.shortDinners} ${w.shortDinners === 1 ? "dinner" : "dinners"}` : null,
          steps !== null ? `${fmt(steps)} steps today` : null,
        ].filter(Boolean);
        return (
          <>
            {!synced ? (
              <Notice tone="info">
                Nothing from {name}’s phone yet. {p.Their} plan
                appears here once Kaya on it has synced.
              </Notice>
            ) : null}

            <Hero
              label={`${name}’s week`}
              figure={`${w.planned} of 7`}
              unit="days planned"
              line={line.length ? line.join(" · ") : undefined}
            />

            <RowGroup heading={`For ${name}`}>
              <NavRow
                icon={I.calendar}
                title="This week’s plan"
                subtitle={`${w.planned} of 7 days · ${weekRangeLabel(w.from, w.to)}`}
                to={familyPath(id, "plan")}
              />
              <NavRow
                icon={I.legs}
                title="Training"
                subtitle={trainingLine(familyTraining(S, today))}
                to={familyPath(id, "training")}
              />
              <NavRow
                icon={I.target}
                title={`${p.Their} targets`}
                subtitle={[
                  T.kcal !== null ? `~${fmt(Math.round(T.kcal / 10) * 10)} kcal est.` : null,
                  `${fmt(T.protein)} g protein`,
                  T.iron !== null ? `${fmt(T.iron)} mg iron` : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                to={familyPath(id, "plan")}
              />
              <NavRow
                icon={I.bell}
                title="Water and dinner reminders"
                subtitle={`Water: ${remindersSummary(S, today)} · Log dinner?: ${dinner.on ? dinner.at : "Off"}`}
                to={familyPath(id, "reminders")}
              />
            </RowGroup>
          </>
        );
      }}
    </LinkedShell>
  );
}
